import React, {useEffect, useMemo, useState} from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItemButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import {useGetAiDialogsQuery} from "../../core/api/ai-zendesk-api"
import {
    ALL_GAMES,
    CRITICAL_CATEGORIES,
    DIALOG_STATUS_COLOR,
    DIALOG_STATUS_LABEL,
    errorText,
    formatExact,
    formatRelative,
    gameLabel
} from "./aiTicketsUtils"

const ALL_POLL_MS = 30000

const TicketRow = ({dialog, onSelect}) => {
    const pending = dialog.pendingAnswersCount ?? 0
    const lastAt = dialog.lastUserMessageAt ?? dialog.updatedAt
    const critical = dialog.category && CRITICAL_CATEGORIES.has(dialog.category)

    return (
        <ListItemButton
            dense
            divider
            onClick={() => onSelect(String(dialog.ticketId))}
            sx={{display: "block", width: "100%", px: 1.5, py: 1}}
        >
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center", flexWrap: "wrap", rowGap: 0.5}}>
                <Typography variant="body2" sx={{fontWeight: 600}}>#{dialog.ticketId}</Typography>
                <Chip
                    label={DIALOG_STATUS_LABEL[dialog.status] ?? dialog.status}
                    color={DIALOG_STATUS_COLOR[dialog.status] ?? "default"}
                    variant="outlined"
                    sx={{height: 18, fontSize: 11}}
                />
                {pending > 0 && (
                    <Chip label={`${pending} to approve`} color="warning" sx={{height: 18, fontSize: 11}}/>
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.25}}>
                {gameLabel(dialog.game)}
                {dialog.language ? ` · ${dialog.language}` : ""}
                {dialog.channel ? ` · ${dialog.channel.toLowerCase()}` : ""}
            </Typography>
            <Typography variant="caption" color={critical ? "warning.main" : "text.secondary"} component="div">
                {critical ? `${dialog.category} · ` : ""}
                <Tooltip title={formatExact(lastAt)} placement="left">
                    <span>{formatRelative(lastAt)}</span>
                </Tooltip>
            </Typography>
        </ListItemButton>
    )
}

const QueryState = ({query, emptyText, onRetry, children}) => {
    if (query.isLoading) {
        return <Box sx={{display: "flex", justifyContent: "center", p: 3}}><CircularProgress size={24}/></Box>
    }
    if (query.isError) {
        return (
            <Alert
                severity="error"
                sx={{m: 1}}
                action={<Button size="small" color="inherit" variant="text" onClick={onRetry}>Retry</Button>}
            >
                {errorText(query.error)}
            </Alert>
        )
    }
    if (!children || (Array.isArray(children) && children.length === 0)) {
        return <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: "center"}}>{emptyText}</Typography>
    }
    return <List disablePadding sx={{width: "100%"}}>{children}</List>
}

/**
 * Two queues: "Pending" (answers awaiting approval, polled by the parent for the badge)
 * and "All open" (every unclosed dialog, fetched only while this tab is visible).
 * The game filter follows the current section (header), so there is no game picker here.
 */
export default function TicketList({gameFilter, pendingQuery, pendingDialogs, onRetry, onSelect}) {
    const [tab, setTab] = useState("pending")

    const [allPoll, setAllPoll] = useState(ALL_POLL_MS)
    const gameParam = gameFilter === ALL_GAMES ? undefined : gameFilter
    const allQuery = useGetAiDialogsQuery({hasPendingAnswers: false, game: gameParam}, {skip: tab !== "all", pollingInterval: allPoll})
    useEffect(() => {
        setAllPoll(allQuery.isError ? 0 : ALL_POLL_MS)
    }, [allQuery.isError])

    const allDialogs = useMemo(() => {
        const all = Array.isArray(allQuery.data) ? allQuery.data : []
        return gameFilter === ALL_GAMES ? all : all.filter((dialog) => dialog.game === gameFilter)
    }, [allQuery.data, gameFilter])

    const activeQuery = tab === "all" ? allQuery : pendingQuery
    const refresh = () => {
        onRetry()
        if (tab === "all") {
            setAllPoll(ALL_POLL_MS)
            allQuery.refetch()
        }
    }

    return (
        <>
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center", px: 1, pt: 0.5, width: "100%"}}>
                <Typography variant="subtitle2" sx={{flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}}>
                    AI tickets · {gameFilter === ALL_GAMES ? "all games" : gameLabel(gameFilter)}
                </Typography>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={refresh} disabled={activeQuery.isFetching} aria-label="Refresh">
                            {activeQuery.isFetching ? <CircularProgress size={16}/> : <RefreshIcon fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Tabs value={tab} onChange={(e, value) => setTab(value)} variant="fullWidth" sx={{minHeight: 36, width: "100%"}}>
                <Tab value="pending" label={`Pending (${pendingDialogs.length})`} sx={{minHeight: 36, fontSize: 12}}/>
                <Tab value="all" label={allQuery.data ? `All open (${allDialogs.length})` : "All open"} sx={{minHeight: 36, fontSize: 12}}/>
            </Tabs>

            <Box sx={{flex: 1, minHeight: 0, width: "100%", overflowY: "auto"}}>
                {tab === "pending" ? (
                    <QueryState query={pendingQuery} emptyText="No AI answers awaiting approval" onRetry={refresh}>
                        {pendingDialogs.map((dialog) => <TicketRow key={dialog.id} dialog={dialog} onSelect={onSelect}/>)}
                    </QueryState>
                ) : (
                    <QueryState query={allQuery} emptyText="No open dialogs" onRetry={refresh}>
                        {allDialogs.map((dialog) => <TicketRow key={dialog.id} dialog={dialog} onSelect={onSelect}/>)}
                    </QueryState>
                )}
            </Box>
        </>
    )
}
